<template>
    <div>
        <button v-if="hasPermission" @click="handleSendWhatsApp" :disabled="loading || !canSend"
            class="bg-green-500 text-white px-3 py-2 rounded-lg hover:bg-green-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-semibold"
            :title="tooltipMessage">
            <font-awesome-icon v-if="!loading" :icon="['fab', 'whatsapp']" class="text-lg" />
            <svg v-else class="w-4 h-4 animate-spin">
                <use href="@/assets/svg/icon.svg#spinnerForBlue" />
            </svg>
            <span>{{ buttonText }}</span>
        </button>

        <!-- Modal de confirmación con contraseña -->
        <ValidateUserAccess v-if="showPasswordModal" :activeModal="togglePasswordModal" :confirmed="onPasswordConfirmed"
            :idUsuario="userId" areas="ENVIAR WHATSAPP" />
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { errorActions, confirmationOfRecord } from '@/alerts';
import { observer } from '@/observer';
import axios from '@/api';
import ValidateUserAccess from '@/components/ValidateUserAccess/ValidateUserAccess.vue';

export default {
    name: 'SendWhatsAppButton',
    components: {
        ValidateUserAccess
    },
    props: {
        saleId: {
            type: Number,
            required: true
        },
        customerId: {
            type: Number,
            required: true
        },
        customerWhatsapp: {
            type: String,
            default: null
        },
        acceptsWhatsapp: {
            type: Boolean,
            default: false
        },
        pdfUrl: {
            type: String,
            default: null
        },
        requiresPasswordConfirmation: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        const loading = ref(false);
        const hasPermission = ref(false);
        const showPasswordModal = ref(false);
        const userId = ref(null);

        // Verificar si el usuario tiene el permiso al montar el componente
        onMounted(async () => {
            try {
                // Obtener el userId del localStorage (ajusta según tu implementación)
                const userData = JSON.parse(localStorage.getItem('access'));
                userId.value = userData?.userId || userData?.id;

                if (!userId.value) {
                    hasPermission.value = false;
                    return;
                }

                // Verificar si el usuario tiene el permiso "ENVIAR WHATSAPP"
                const response = await axios.get(`/accesos-especificos-de-usuario/${userId.value}`);
                const accesos = response.data.data || [];

                hasPermission.value = accesos.some(
                    acceso => acceso.areas === 'ENVIAR WHATSAPP'
                );
            } catch (error) {
                console.error('Error verificando permisos:', error);
                hasPermission.value = false;
            }
        });

        const canSend = computed(() => {
            return props.acceptsWhatsapp && props.customerWhatsapp;
        });

        const buttonText = computed(() => {
            if (loading.value) return 'Enviando...';
            return 'WhatsApp';
        });

        const tooltipMessage = computed(() => {
            if (!canSend.value) {
                if (!props.customerWhatsapp) {
                    return 'Cliente sin WhatsApp registrado';
                }
                if (!props.acceptsWhatsapp) {
                    return 'Cliente no acepta WhatsApp';
                }
            }
            return 'Enviar factura por WhatsApp';
        });

        const togglePasswordModal = () => {
            showPasswordModal.value = !showPasswordModal.value;
        };

        const onPasswordConfirmed = () => {
            showPasswordModal.value = false;
            sendWhatsAppMessage();
        };

        const handleSendWhatsApp = () => {
            if (!canSend.value) {
                errorActions(tooltipMessage.value);
                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
                return;
            }

            // Si requiere confirmación de contraseña, mostrar modal
            if (props.requiresPasswordConfirmation) {
                showPasswordModal.value = true;
            } else {
                sendWhatsAppMessage();
            }
        };

        const sendWhatsAppMessage = async () => {
            loading.value = true;

            try {
                const response = await axios.post('/whatsapp/send', {
                    idVenta: props.saleId,
                    idUsuario: userId.value,
                    pdf_url: props.pdfUrl
                });

                if (response.data.success) {
                    confirmationOfRecord('Mensaje de WhatsApp enviado exitosamente');
                } else {
                    errorActions(response.data.message || 'Error al enviar mensaje');
                }

                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } catch (error) {
                console.error('Error enviando WhatsApp:', error);
                const errorMessage = error.response?.data?.message || 'Error al enviar mensaje por WhatsApp';
                errorActions(errorMessage);
                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } finally {
                loading.value = false;
            }
        };

        return {
            loading,
            hasPermission,
            showPasswordModal,
            userId,
            canSend,
            buttonText,
            tooltipMessage,
            handleSendWhatsApp,
            togglePasswordModal,
            onPasswordConfirmed
        };
    }
};
</script>

<style scoped>
button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}
</style>
